import { useMemo, FC } from 'react';
import styled from 'styled-components';
import { Button, ButtonProps, CircularProgress } from '@mui/material';
import {
  Associate,
  AssociateApprovalStatus,
  useChangeAssociateAvailability,
} from '@vs-online-only-app/state';
import { IconChat, IconChatOffline } from '@vs-online-only-app/ui';

const StyledButton = styled(Button)`
  grid-template-columns: 20px 1fr;
  gap: ${({ theme }) => theme.spacing(1)};
  min-width: 150px;
  white-space: nowrap;
  align-items: center;
`;

interface Props extends ButtonProps {
  associate?: Associate;
}

const UpdateAssociateAvailabilityButton: FC<Props> = ({
  variant,
  disabled,
  associate,
  ...props
}) => {
  const { isLoading, mutateAsync } = useChangeAssociateAvailability();

  const isAssociateAvailable = associate?.available;
  const isApproved =
    associate?.approvalStatus === AssociateApprovalStatus.Approved;

  const buttonText = useMemo(() => {
    if (!isApproved) {
      return 'Waiting to be approved';
    }
    if (isLoading) {
      return <CircularProgress size={16} sx={{ margin: 'auto' }} />;
    }
    if (isAssociateAvailable) {
      return 'Go offline';
    }
    return 'Go online';
  }, [isApproved, isLoading, isAssociateAvailable]);

  const icon = useMemo(() => {
    if (!isApproved) return null;
    if (isAssociateAvailable) return <IconChatOffline />;
    return <IconChat />;
  }, [isApproved, isAssociateAvailable]);

  const handleClick = () => {
    mutateAsync(!isAssociateAvailable);
  };

  if (!associate) {
    return null;
  }

  const isDisabled = !isApproved || isLoading || Boolean(disabled);

  return (
    <StyledButton
      variant={variant}
      onClick={handleClick}
      disabled={isDisabled}
      color={isAssociateAvailable ? 'secondary' : 'primary'}
      disableElevation={isAssociateAvailable}
      {...props}
    >
      {!isDisabled && icon}
      {buttonText}
    </StyledButton>
  );
};

export default UpdateAssociateAvailabilityButton;
